import classNames from 'classnames';
import { InputHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Input from './Input';
import { DateRange, DayPicker, getDefaultClassNames } from 'react-day-picker';

import './DatePicker.css';

import { CalendarDaysIcon, ClockIcon } from 'lucide-react';
import { getCurrentCopenhagenTime } from 'utils/dateTime';
import Button from './Button';

interface DateTimePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  className?: string;
  inputClassName?: string;
  error?: string | boolean;
  value: DateRange | undefined;
  label?: string | React.ReactElement;
  onChange?: (newDate: DateRange | undefined, name: string) => void;
  maxRange?: number; // Maximum range in days
}

const toUtcDateKey = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toUtcTimeKey = (date: Date): string => {
  const hours = `${date.getUTCHours()}`.padStart(2, '0');
  const minutes = `${date.getUTCMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
};

const dateFormatter = Intl.DateTimeFormat('en', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
});

function DateTimePickerRange({
  className,
  value,
  onChange,
  onBlur,
  name,
  maxRange,
  ...rest
}: DateTimePickerProps) {
  const defaultClassNames = getDefaultClassNames();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const datePickerRef = useRef<HTMLDivElement | null>(null); // Reference for DayPicker container
  const datePickerInputRef = useRef<HTMLLabelElement | null>(null); // Reference for DayPicker input

  const [selectedMonth, setSelectedMonth] = useState(value?.from ?? new Date());

  const [selectedFromTime, setSelectedFromTime] = useState<string>(
    value?.from ? toUtcTimeKey(value.from) : getCurrentCopenhagenTime()
  );
  const [selectedToTime, setSelectedToTime] = useState<string>(
    value?.to ? toUtcTimeKey(value.to) : getCurrentCopenhagenTime()
  );

  const handleDayPickerSelect = (
    newDate: DateRange | undefined,
    newFromTime?: string,
    newToTime?: string
  ) => {
    if (onChange) {
      if (newDate?.from) {
        const ts = newFromTime ?? selectedFromTime;
        newDate.from = new Date(toUtcDateKey(newDate.from) + `T${ts}Z`);
      }
      if (newDate?.to) {
        const ts = newToTime ?? selectedToTime;
        newDate.to = new Date(toUtcDateKey(newDate.to) + `T${ts}Z`);
      }
      onChange(newDate, name ?? '');
    }
  };

  const handleFromTimeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newFromTime = e.target.value;
    setSelectedFromTime(newFromTime);
    if (value?.from) {
      handleDayPickerSelect(
        {
          from: new Date(toUtcDateKey(value.from) + `T${newFromTime}Z`),
          to: value?.to,
        },
        newFromTime,
        selectedToTime
      );
    }
  };

  const handleToTimeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newToTime = e.target.value;
    setSelectedToTime(newToTime);
    if (value?.to) {
      handleDayPickerSelect(
        {
          from: value?.from,
          to: new Date(toUtcDateKey(value.to) + `T${newToTime}Z`),
        },
        selectedFromTime,
        newToTime
      );
    }
  };

  const handleSetFullDay = () => {
    if (!value?.from) {
      return;
    }
    setSelectedFromTime('00:00');
    setSelectedToTime('23:59');
    handleDayPickerSelect({ from: value.from, to: value.to ?? value.from }, '00:00', '23:59');
  };

  const handleSetToday = () => {
    const today = new Date();
    setSelectedFromTime('00:00');
    setSelectedToTime('23:59');
    setSelectedMonth(today);
    handleDayPickerSelect({ from: today, to: today }, '00:00', '23:59');
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        datePickerInputRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        !datePickerInputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close the date picker if click is outside
        if (onBlur) {
          // Trigger onBlur event if provided
          onBlur(new Event('blur') as unknown as React.FocusEvent<HTMLInputElement>);
        }
      }
    },
    [onBlur]
  );

  // Handle clicking outside of the date picker to close it
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  const formattedValue = useMemo(() => {
    if (!value || !value.from) {
      return '';
    }

    const v: string = value?.from
      ? dateFormatter.formatRange(value.from, value?.to ?? value.from)
      : '';
    return v;
  }, [value]);

  return (
    <div className={classNames('relative', className)} data-component="DateTimePickerRange">
      <Input
        labelIcon={
          <span className="text-heading inline-block">
            <CalendarDaysIcon size={16} />
          </span>
        }
        onFocus={() => {
          setIsOpen(true);
        }}
        autoComplete="off"
        labelRef={datePickerInputRef}
        value={formattedValue}
        onChange={() => {
          if (onChange) {
            handleDayPickerSelect(undefined);
          }
        }}
        readOnly
        clearable
        focusOnClear={false}
        {...rest}
      />
      {isOpen && (
        <div
          ref={datePickerRef}
          className="absolute z-10 rounded-md border-2 border-neutral-500 bg-white px-3"
        >
          <DayPicker
            timeZone="UTC"
            captionLayout="dropdown"
            mode="range"
            selected={{
              from: value?.from ? new Date(value.from) : undefined,
              to: value?.to ? new Date(value.to) : undefined,
            }}
            onMonthChange={(newMonth) => {
              setSelectedMonth(newMonth);
            }}
            disabled={{ after: new Date() }}
            fixedWeeks
            weekStartsOn={1}
            onSelect={(newDate: DateRange | undefined) =>
              handleDayPickerSelect(newDate, selectedFromTime, selectedToTime)
            }
            showOutsideDays
            required={false}
            month={selectedMonth}
            max={maxRange}
            classNames={{
              // day: 'rounded-md',
              range_start: 'range-start bg-blue-600 text-white rounded-l-md', // Highlight the start of the range
              range_end: 'range-end bg-blue-600 text-white rounded-r-md', // Highlight the end of the range
              range_middle: `bg-blue-100 rounded-none text-neutral-800`, // Highlight the range of selected days
              today: `rounded-md bg-blue-50 underline`, // Add a border to today's date
              selected: ``, // Highlight the selected day
              // root: `${defaultClassNames.root} shadow-lg p-5`, // Add a shadow to the root element
              chevron: `fill-blue-600`, // Change the color of the chevron
              // month: 'text-heading',
              button_previous: 'text-green-800 fill-red-800',
              caption_label: `${defaultClassNames.caption_label} text-neutral-800`,
            }}
          />

          <div className="mt-1 mb-2 flex items-center justify-around gap-4 border-t border-neutral-200 pt-3 text-neutral-800">
            <label className="flex flex-col items-start gap-2 text-sm font-semibold">
              <div className="flex items-start gap-2">
                <ClockIcon size={16} className="text-neutral-800" />
                Start
              </div>
              <input
                type="time"
                value={selectedFromTime}
                onChange={handleFromTimeChange}
                className="h-9 rounded border border-neutral-500 bg-white px-2 font-normal text-neutral-800 outline-offset-1 outline-none focus:outline-4 focus:outline-blue-200"
              />
            </label>
            <label className="flex flex-col items-start gap-2 text-sm font-semibold">
              <div className="flex items-start gap-2">
                <ClockIcon size={16} className="text-neutral-800" />
                End
              </div>
              <input
                type="time"
                value={selectedToTime}
                onChange={handleToTimeChange}
                className="h-9 rounded border border-neutral-500 bg-white px-2 font-normal text-neutral-800 outline-offset-1 outline-none focus:outline-4 focus:outline-blue-200"
              />
            </label>
          </div>

          <hr className="border-neutral-200" />

          <div className="mt-3 mb-2 flex items-center justify-around gap-4 text-neutral-800">
            <Button variant="secondary" disabled={!value?.from} onClick={handleSetFullDay}>
              00:00 - 23:59
            </Button>
            <Button variant="secondary" onClick={handleSetToday}>
              Today
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateTimePickerRange;
